// src/pages/dashboard/Dashboard.js
import React from 'react';
import { Star, Trophy, Book, PenTool, Volume2, MessageCircle, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import SectionCard from '../../components/common/SectionCard';

const Dashboard = ({ onNavigate, vocabularyCount = 0 }) => {
  const { currentUser, userProgress } = useAuth();

  const progressPercentage = Math.min(100, Math.floor((userProgress.total_points / 500) * 100));

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-b-3xl shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">ChineseMaster</h1>
            <p className="text-red-100 mt-1">Nível {userProgress.level} • HSK 1</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="flex items-center gap-2 text-yellow-300">
                <Star className="w-5 h-5 fill-current" />
                <span className="font-bold">{userProgress.total_points}</span>
              </div>
              <p className="text-red-100 text-sm">{userProgress.streak_days} dias seguidos</p>
            </div>
            <button
              onClick={() => onNavigate('profile')}
              className="bg-red-500/30 p-3 rounded-full hover:bg-red-500/50 transition-colors"
            >
              <User className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-6 bg-red-500/30 rounded-2xl p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-red-100">Progresso Geral</span>
            <span className="text-white font-bold">{progressPercentage}%</span>
          </div>
          <div className="w-full bg-red-400/30 rounded-full h-3">
            <div 
              className="bg-yellow-400 h-3 rounded-full transition-all duration-500" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
        
        {/* Welcome Message */}
        <div className="mt-4 text-center">
          <p className="text-red-100">
            Olá, <span className="font-bold text-white">{currentUser?.name}</span>!
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Escolha uma seção</h2>
        
        {/* Section Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SectionCard
            icon={<Book className="w-8 h-8" />}
            title="Vocabulário"
            description={`${vocabularyCount} palavras disponíveis`}
            color="bg-blue-500"
            onClick={() => onNavigate('vocabulary')}
          />
          
          <SectionCard
            icon={<PenTool className="w-8 h-8" />}
            title="Caracteres"
            description="Pratique a escrita chinesa"
            color="bg-green-500"
            onClick={() => onNavigate('characters')}
          />
          
          <SectionCard
            icon={<Volume2 className="w-8 h-8" />}
            title="Pronúncia"
            description="Melhore sua fala"
            color="bg-purple-500"
            onClick={() => onNavigate('pronunciation')}
          />
          
          <SectionCard
            icon={<MessageCircle className="w-8 h-8" />}
            title="Conversação"
            description="Diálogos práticos"
            color="bg-orange-500"
            onClick={() => onNavigate('conversation')}
          />
        </div>

        {/* Achievements Section */}
        <div className="mt-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Suas Conquistas</h3>
          <div className="flex gap-4 flex-wrap">
            {userProgress.badges && userProgress.badges.length > 0 ? (
              userProgress.badges.map((badge, index) => (
                <div key={index} className="bg-yellow-100 p-4 rounded-2xl border-2 border-yellow-300">
                  <Trophy className="w-8 h-8 text-yellow-600 mb-2 mx-auto" />
                  <p className="font-bold text-yellow-800 text-center">{badge.name}</p>
                  {badge.description && (
                    <p className="text-xs text-yellow-600 text-center mt-1">{badge.description}</p>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-gray-100 p-4 rounded-2xl border-2 border-gray-300">
                <Trophy className="w-8 h-8 text-gray-400 mb-2 mx-auto" />
                <p className="font-bold text-gray-600 text-center">
                  Continue estudando para ganhar badges!
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-2xl shadow-lg text-center">
            <div className="text-2xl font-bold text-blue-600">{userProgress.completed_lessons}</div>
            <div className="text-sm text-gray-600">Lições Completas</div>
          </div>
          
          <div className="bg-white p-4 rounded-2xl shadow-lg text-center">
            <div className="text-2xl font-bold text-green-600">{userProgress.level}</div>
            <div className="text-sm text-gray-600">Nível HSK</div>
          </div>
          
          <div className="bg-white p-4 rounded-2xl shadow-lg text-center">
            <div className="text-2xl font-bold text-yellow-600">{userProgress.total_points}</div>
            <div className="text-sm text-gray-600">Pontos Totais</div>
          </div>
          
          <div className="bg-white p-4 rounded-2xl shadow-lg text-center">
            <div className="text-2xl font-bold text-orange-600">{userProgress.streak_days}</div>
            <div className="text-sm text-gray-600">Dias Seguidos</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;